/**
 * Web-side Invite Code Signup Flow
 * 
 * This page handles invite code signups completely web-side when:
 * - invite_code_system_enabled is false (default)
 * - OR mobile_first_onboarding_enabled is false
 * 
 * Flow:
 * 1. User enters 6-digit invite code
 * 2. System decodes code to get company_id, location_id, draft_student_id
 * 3. User signs up with email/password
 * 4. Account created and linked to draft record
 * 5. User completes onboarding web-side
 * 6. Redirect to app download/login
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { routeInviteRequest } from '../../lib/inviteCodeRouter';
import { detectPlatform } from '../DeepLinkUtils';

interface InviteCodeData {
  company_id: string;
  location_id?: string;
  draft_student_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}

export default function InviteCodeSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codeParam = searchParams.get('code');
  
  const [code, setCode] = useState(codeParam || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'code' | 'signup'>('code');
  const [inviteData, setInviteData] = useState<InviteCodeData | null>(null);

  // Check feature toggle on mount - use router for version-compatible routing
  useEffect(() => {
    const checkFeatureToggle = async () => {
      if (!codeParam) return;
      
      const platform = detectPlatform();
      const params = new URLSearchParams();
      params.set('code', codeParam);
      
      const routing = await routeInviteRequest(params, platform);
      
      if (routing.shouldRedirectToMobile && routing.mobileDeepLink) {
        // Feature toggle enabled - redirect to mobile app
        window.location.replace(routing.mobileDeepLink);
      }
      // If web-side handling, continue with current flow
    };
    
    if (codeParam) {
      checkFeatureToggle();
    }
  }, [codeParam]);

  const handleDecodeCode = async () => {
    if (!code.trim() || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use type assertion because RPC function may not be in TypeScript types yet
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: codeData, error: codeError } = await (supabase.rpc as any)('decode_invite_code', {
        p_code: code.trim().toUpperCase(),
      });

      if (codeError) {
        setError('Failed to process invite code. Please try again.');
        setLoading(false);
        return;
      }

      // Type the response as the expected JSON structure
      const decoded = codeData as {
        success?: boolean;
        found?: boolean;
        company_id?: string;
        location_id?: string;
        draft_student_id?: string;
        email?: string;
        first_name?: string;
        last_name?: string;
        phone_number?: string;
        status?: string;
        error?: string;
      } | null;

      if (!decoded || !decoded.found) {
        setError('This invite code is not valid or has expired.');
        setLoading(false);
        return;
      }

      // Store invite data and move to signup step
      setInviteData({
        company_id: decoded.company_id || '',
        location_id: decoded.location_id,
        draft_student_id: decoded.draft_student_id,
        email: decoded.email,
        first_name: decoded.first_name,
        last_name: decoded.last_name,
      });

      // Pre-fill email and name if available from draft
      if (decoded.email) setEmail(decoded.email);
      if (decoded.first_name) setFirstName(decoded.first_name);
      if (decoded.last_name) setLastName(decoded.last_name);

      setStep('signup');
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }

    if (!inviteData?.company_id) {
      setError('Invalid invite code data. Please try again.');
      return;
    }

    setLoading(true);

    try {
      // Sign up user with invite code context
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            signup_intent: 'student_with_invite',
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            company_id: inviteData.company_id,
            location_id: inviteData.location_id,
            draft_student_id: inviteData.draft_student_id,
          },
        },
      });

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      if (!authData.user) {
        setError('Failed to create account. Please try again.');
        setLoading(false);
        return;
      }

      // Check if email confirmation is required
      if (authData.session) {
        // Logged in immediately - proceed to onboarding
        navigate('/student-onboarding');
      } else {
        // Email confirmation required. A key, not free text: Signin renders
        // a fixed notice for it (the old `?message=` was never read, so the
        // user landed on a bare login form and then hit "Invalid email or
        // password" for the unconfirmed account — 2026-10-06).
        setError(null);
        navigate('/signin?notice=confirm-email');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'code') {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
        <Card className="w-full max-w-sm sm:max-w-md border-[#504A4A] bg-[#221F1F]">
          <CardHeader>
            <CardTitle className="text-white text-center text-lg sm:text-xl">Enter Your Invite Code</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-300 text-center mb-6 text-sm sm:text-base">
              Enter the 6-digit code you received from your gym
            </p>

            <div className="mb-6">
              <Input
                type="text"
                placeholder="ABC123"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
                  setCode(value);
                  setError(null);
                }}
                className="text-center text-xl sm:text-2xl font-mono tracking-widest px-3 sm:px-4 py-2 sm:py-2.5 bg-[#504A4A] border-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3]"
                maxLength={6}
                autoCapitalize="characters"
                autoCorrect="off"
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            )}

            <Button
              onClick={handleDecodeCode}
              disabled={loading || code.length !== 6}
              className="w-full bg-[#e11d48] hover:bg-[#be1e3e] mb-4"
            >
              {loading ? 'Processing...' : 'Continue'}
            </Button>

            <div className="text-center">
              <button
                onClick={() => navigate('/signin')}
                className="text-sm text-[#00D9FF] hover:text-[#00B8D9]"
              >
                Already have an account? Sign in
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
      <Card className="w-full max-w-sm sm:max-w-md border-[#504A4A] bg-[#221F1F]">
        <CardHeader>
          <CardTitle className="text-white text-center text-lg sm:text-xl">Create Your Account</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
                maxLength={254}
                className="text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                First Name
              </label>
              <Input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                required
                disabled={loading}
                maxLength={30}
                className="text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                Last Name
              </label>
              <Input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                required
                disabled={loading}
                maxLength={30}
                className="text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
                disabled={loading}
                maxLength={128}
                className="text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                Confirm Password
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                disabled={loading}
                className="text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setStep('code')}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#e11d48] hover:bg-[#be1e3e]"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

